import { useRef } from 'react';
import { ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';

import { MediaFrame, Magnetic, PointerParallax, SplitReveal } from '@/components/motion';
import { useReducedMotion } from '@/hooks/use-reduced-motion';
import { gsap, useGSAP } from '@/lib/gsap';
import { DURATION, GSAP_EASE } from '@/lib/motion-tokens';
import { Button, cn } from '@ibas/ui';
import { brand } from '@/config/brand';

// IBAS's flagship "Study your way" homepage film, with the Thalwil campus
// building as the poster frame shown before playback / under reduced motion.
const HERO_VIDEO = '/media/videos/study-your-way-homepage.mp4';
const HERO_POSTER = '/media/images/campus/campus-building-exterior.webp';

export interface CinematicHeroProps {
  className?: string;
  /** Optional id for in-page anchoring. */
  id?: string;
}

/**
 * CinematicHero — the site's flagship hero: a full-viewport cinematic stage.
 *
 * Visual: a MediaFrame video plate fills the section. The film carries burned-in
 * titling, so a navy wash sits over it at a meaningful base strength from the
 * first frame (~65% effective navy) — enough for that titling to recede behind
 * the HTML headline at load — then deepens further on scroll to keep the white
 * type legible (poster-only under reduced motion, per the brand "no disruptive
 * autoplay" rule; wash at full strength there so the static hero stays readable).
 * As the copy reveals, the FILM (not the wash) is blurred to blur(12px) so the
 * sharp footage stops competing with the headline; the navy wash stays crisp and
 * its solid layer sits at bg-brand-navy/76, so the revealed state reads at ~76%
 * effective navy and the white type pops. Only the film is wrapped in the blur
 * layer — the wash is a sibling, so the blur never softens the navy itself.
 *
 * Interactivity (two ideas, both tied to meaning):
 *  - Pointer: the foreground layers sit inside PointerParallax and drift by depth
 *    (eyebrow 0.4 → headline 0.9 → actions 1.3), so the composition gains real
 *    spatial depth as the cursor moves. Disabled on touch / reduced motion.
 *  - Scroll: the hero PINS and a scrubbed ScrollTrigger timeline runs against
 *    that pin in two beats — first the copy rises and fades in over the still
 *    SHARP film; then, once the message is present, the film blurs and the navy
 *    wash deepens so the white type owns the frame. The copy then HOLDS (no exit
 *    — once shown it stays) until the hero releases and scrolls away. The video
 *    plate ken-burns gently behind it throughout.
 *
 * Reduced motion: no pin, no parallax, no scrub; MediaFrame shows the poster and
 * the content renders static, centred and fully readable (never hidden).
 */
export function CinematicHero({ className, id }: CinematicHeroProps) {
  const sectionRef = useRef<HTMLElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const mediaRef = useRef<HTMLDivElement>(null);
  const filmRef = useRef<HTMLDivElement>(null);
  const overlayRef = useRef<HTMLDivElement>(null);
  const cueRef = useRef<HTMLDivElement>(null);
  const reduced = useReducedMotion();

  useGSAP(
    () => {
      const section = sectionRef.current;
      const content = contentRef.current;
      if (!section || !content || reduced) return;

      // The message layers (eyebrow → headline → subtext → actions). We fade
      // these (opacity only — their transforms belong to PointerParallax) while
      // the wrapper carries the shared rise, so the two never fight.
      const layers = Array.from(content.children);

      // Start with the copy hidden but the navy wash ALREADY at its base
      // strength (0.85 of full ⇒ ~58% effective navy), so the film's burned-in
      // titling recedes behind the HTML headline from the first frame. Set
      // synchronously inside useGSAP (pre-paint ⇒ no flash) and NEVER on the
      // reduced-motion path above, so opt-out users always get the full, static,
      // legible hero. The reveal is scrubbed to a pinned ScrollTrigger —
      // position-driven, so it can never strand the copy hidden.
      gsap.set(content, { y: 56 });
      gsap.set(layers, { autoAlpha: 0 });
      gsap.set(overlayRef.current, { opacity: 0.85 });
      // Film starts SHARP and stays sharp while the copy reveals; it only blurs
      // afterwards (beat 2 below). Overriding the CSS-default blur (which is what
      // reduced-motion users keep) only on this motion path.
      gsap.set(filmRef.current, { filter: 'blur(0px)' });

      const tl = gsap.timeline({
        defaults: { ease: 'none' },
        scrollTrigger: {
          trigger: section,
          start: 'top top',
          end: '+=100%',
          scrub: 1,
          pin: true,
          anticipatePin: 1,
        },
      });

      // Two-beat scrub, ordered so the film only blurs AFTER the copy is in:
      //   Beat 1 (≈0 → 0.45): the copy rises and fades in over the still-SHARP
      //     film — the cinematic footage reads clearly while the message arrives.
      //   Beat 2 (≈0.5 → 0.95): now that the headline is present, the film blurs
      //     (blur(0px) → blur(12px)) and the navy wash deepens (~65% → ~76%),
      //     settling the footage back so the white type owns the frame.
      // There is NO exit — once revealed the message HOLDS, then the whole hero
      // releases and scrolls away. The film ken-burns gently throughout.
      tl.to(mediaRef.current, { yPercent: 8, scale: 1.06, duration: 1 }, 0)
        .to(content, { y: 0, duration: 0.4 }, 0)
        .to(layers, { autoAlpha: 1, stagger: 0.08, duration: 0.4 }, 0.05)
        .to(cueRef.current, { autoAlpha: 0, duration: 0.2 }, 0.15)
        .to(filmRef.current, { filter: 'blur(12px)', duration: 0.45 }, 0.5)
        .to(overlayRef.current, { opacity: 1, duration: 0.45 }, 0.5);

      // Gentle, endless breathing on the scroll cue — invites the first scroll.
      if (cueRef.current) {
        gsap.to(cueRef.current, {
          y: 8,
          repeat: -1,
          yoyo: true,
          duration: DURATION.slow,
          ease: GSAP_EASE.inOut,
        });
      }
    },
    { scope: sectionRef, dependencies: [reduced] },
  );

  return (
    <section
      ref={sectionRef}
      id={id}
      className={cn(
        'relative isolate flex min-h-dvh w-full items-center overflow-hidden bg-primary',
        className,
      )}
    >
      {/* Background video plate. The film plays sharp at first, then blurs as the
          copy reveals while the navy wash below deepens — together they keep the
          white type legible over the softened footage. */}
      <div ref={mediaRef} aria-hidden={false} className="absolute inset-0 -z-10 h-full w-full">
        {/* Film blur layer — ONLY the footage lives here, so the blur never
            touches the navy wash (a sibling below). It defaults to blur(12px)
            (the revealed end-state) so reduced-motion users, whose copy is always
            shown, get the matching softened look; the motion path resets it to
            blur(0px) at the top of the pin and animates back to 12px on reveal.
            scale-105 + overflow-hidden keep the blur from exposing soft edges. */}
        <div
          ref={filmRef}
          className="h-full w-full scale-105 overflow-hidden [filter:blur(12px)]"
        >
          <MediaFrame
            image={HERO_POSTER}
            video={HERO_VIDEO}
            alt="Exterior of the copper-clad International Business Academy of Switzerland building in Thalwil under a blue sky"
            aspect="h-full"
            overlay={0}
            reveal={false}
            rounded="rounded-none"
            className="h-full"
          />
        </div>
        {/* Scroll-driven navy wash — a SIBLING of the film blur layer, so it
            stays perfectly crisp. Sits at a meaningful base strength at rest
            (GSAP sets the wrapper to 0.85 ⇒ ~65% effective navy) so the film's
            burned-in titling recedes, then its opacity climbs to full as the
            copy reveals (driven above ⇒ ~76% navy). Rendered at full strength
            under reduced motion (never animated), so the static hero keeps its
            legibility. The stronger revealed navy pairs with the blurred film to
            make the white headline pop; the at-rest state stays light enough that
            the film is never lost. */}
        <div ref={overlayRef} aria-hidden className="pointer-events-none absolute inset-0">
          <div className="absolute inset-0 bg-brand-navy/76" />
          <div className="absolute inset-0 bg-gradient-to-t from-brand-navy/70 via-transparent to-brand-navy/25" />
        </div>
      </div>

      {/* Foreground content — pointer-parallaxed by depth. */}
      <div className="relative z-10 mx-auto w-full max-w-6xl px-6 py-24 md:py-28">
        <PointerParallax strength={28}>
          <div ref={contentRef} className="max-w-3xl">
            <p
              data-parallax
              data-depth="0.4"
              className="font-label text-xs tracking-[0.24em] text-brand-gold-light uppercase md:text-sm"
            >
              {brand.legalName}
            </p>

            <div data-parallax data-depth="0.9">
              <SplitReveal
                as="h1"
                by="lines"
                className="mt-5 font-display text-5xl leading-[1.05] font-semibold text-white md:text-7xl"
              >
                Study your way. Connect globally.
              </SplitReveal>
            </div>

            <p
              data-parallax
              data-depth="0.9"
              className="mt-6 max-w-xl text-lg leading-relaxed text-white/80 md:text-xl"
            >
              Accessible Swiss quality for global career growth.
            </p>

            <div
              data-parallax
              data-depth="1.3"
              className="mt-10 flex flex-col gap-4 sm:flex-row sm:items-center"
            >
              <Magnetic strength={0.4} radius={140}>
                <Button asChild size="lg" data-cursor className="group">
                  <Link to="/programmes">
                    Explore our programmes
                    <ArrowRight
                      aria-hidden
                      className="size-4 transition-transform duration-300 group-hover:translate-x-1 motion-reduce:transition-none"
                    />
                  </Link>
                </Button>
              </Magnetic>

              <Button
                asChild
                size="lg"
                variant="outline"
                className="border-white/30 bg-transparent text-white hover:bg-white/10 hover:text-white"
              >
                <Link to="/admissions#find-advisor">Connect with an adviser</Link>
              </Button>
            </div>
          </div>
        </PointerParallax>
      </div>

      {/* Scroll cue. */}
      <div
        ref={cueRef}
        className="pointer-events-none absolute inset-x-0 bottom-8 z-10 flex flex-col items-center gap-2"
      >
        <span className="font-label text-[0.65rem] tracking-[0.3em] text-white/60 uppercase">
          Scroll
        </span>
        <span
          aria-hidden
          className="flex h-10 w-6 justify-center rounded-full border border-white/30 p-1.5"
        >
          <span className="h-2 w-1 rounded-full bg-brand-gold-light" />
        </span>
      </div>
    </section>
  );
}
